import { useEffect, useState } from "react";
import { Card, CardContent } from "../../../ui/card";
import { Switch } from "../../../ui/switch";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { toast } from "../../../../hooks/use-toast";
import {
  AutomationSequencePanel,
  AUTOMATION_SEQUENCES,
  type AutomationSequenceDef,
} from "../components/automations/AutomationSequencePanel";

type AutomationDef = {
  trigger: string;
  emoji: string;
  name: string;
  description: string;
  emailCount: number;
};

const AUTOMATIONS: AutomationDef[] = [
  {
    trigger: "new_customer",
    emoji: "👋",
    name: "New Customer Onboarding",
    description: "Welcome new customers with a 3-email sequence",
    emailCount: 3,
  },
  {
    trigger: "post_action",
    emoji: "🙏",
    name: "Post-Action Follow-up",
    description: "Thank customers after orders, reviews, or redemptions",
    emailCount: 1,
  },
  {
    trigger: "inactive_30d",
    emoji: "💤",
    name: "Re-engagement (30 days)",
    description: "Win back inactive customers after 30 days",
    emailCount: 2,
  },
];

export default function Automations() {
  const { merchant } = useMerchant();
  const [activeMap, setActiveMap] = useState<Record<string, { id: string; is_active: boolean } | null>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [panelDef, setPanelDef] = useState<AutomationSequenceDef | null>(null);

  useEffect(() => {
    if (!merchant) return;
    const load = async () => {
      setLoading(true);
      const { data } = await epicengageSupabasev1
        .from("automation_rules")
        .select("id, trigger, is_active")
        .eq("merchant_id", merchant.id);
      const map: Record<string, { id: string; is_active: boolean } | null> = {};
      AUTOMATIONS.forEach((a) => {
        const found = data?.find((r) => r.trigger === a.trigger);
        map[a.trigger] = found ? { id: found.id, is_active: !!found.is_active } : null;
      });
      setActiveMap(map);
      setLoading(false);
    };
    load();
  }, [merchant]);

  const toggle = async (def: AutomationDef, next: boolean) => {
    if (!merchant) return;
    setBusy(def.trigger);
    const existing = activeMap[def.trigger];
    if (existing) {
      const { error } = await epicengageSupabasev1
        .from("automation_rules")
        .update({ is_active: next })
        .eq("id", existing.id);
      if (error) {
        toast({ title: "Failed to update", description: error.message, variant: "destructive" });
      } else {
        setActiveMap((m) => ({ ...m, [def.trigger]: { ...existing, is_active: next } }));
        toast({ title: next ? "Automation activated" : "Automation paused" });
      }
    } else {
      const { data, error } = await epicengageSupabasev1
        .from("automation_rules")
        .insert({ merchant_id: merchant.id, trigger: def.trigger, is_active: next })
        .select("id, is_active")
        .single();
      if (error || !data) {
        toast({ title: "Failed to create", description: error?.message, variant: "destructive" });
      } else {
        setActiveMap((m) => ({ ...m, [def.trigger]: { id: data.id, is_active: !!data.is_active } }));
        toast({ title: next ? "Automation activated" : "Automation created" });
      }
    }
    setBusy(null);
  };

  return (
    <div>
      <div className="px-10 py-10">
        <h1 className="text-2xl font-semibold tracking-tight">Automations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Trigger-based email flows that run automatically in the background
        </p>

        <div className="mt-8 space-y-3">
          {AUTOMATIONS.map((a) => {
            const state = activeMap[a.trigger];
            const isActive = !!state?.is_active;
            return (
              <Card key={a.trigger}>
                <CardContent className="p-5 flex items-center gap-5">
                  <div className="text-3xl leading-none">{a.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{a.name}</h3>
                      <Badge variant={isActive ? "default" : "secondary"}>
                        {isActive ? "Active" : "Ready"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{a.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {a.emailCount} email{a.emailCount > 1 ? "s" : ""} in sequence
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPanelDef(AUTOMATION_SEQUENCES[a.trigger] ?? null)}
                  >
                    View emails
                  </Button>
                  <Switch
                    checked={isActive}
                    disabled={loading || busy === a.trigger}
                    onCheckedChange={(v) => toggle(a, v)}
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      <AutomationSequencePanel
        open={!!panelDef}
        onOpenChange={(v) => !v && setPanelDef(null)}
        def={panelDef}
      />
    </div>
  );
}
